/* eslint-disable max-classes-per-file */
import { cloneDeep, uniq } from 'lodash';
import { DateTime } from 'luxon';
import type { ByWeekday, Weekday } from 'rrule';
import { Frequency, RRule } from 'rrule';
import { v4 as uuid } from 'uuid';

import type { Category, Item, LiveProgram } from '../../models';
import { CustomAttributes } from '../../models/customAttributes';
import calendarUtils from '../../utils/calendarUtils';
import clientUtils from '../../utils/clientUtils';
import { compressionUtils } from '../../utils/compressionUtils';
import { dateUtils } from '../../utils/dateUtils';
import FuelCalendarHelper from './FuelCalendarHelper';

export interface extendedProps {
  description?: string;
  timezone: string;
  categories: Category[];
}

export enum RruleFrequency {
  YEARLY = 'year',
  MONTHLY = 'monthly',
  WEEKLY = 'weekly',
  DAILY = 'daily',
}

export class RruleOptions {
  freq: Frequency = Frequency.DAILY;
  dtstart: Date | null = null;
  interval: number = 1;
  wkst: Weekday | number | null = null;
  count: number | null = null;
  until: Date | null = null;
  tzid: string | null = null;
  bysetpos: number | number[] | null = null;
  bymonth: number | number[] | null = null;
  bymonthday: number | number[] | null = null;
  bynmonthday: number[] | null = null;
  byyearday: number | number[] | null = null;
  byweekno: number | number[] | null;
  byweekday: ByWeekday | ByWeekday[] | null = null;
  bynweekday: number[][] | null = null;
  byhour: number | number[] | null = null;
  byminute: number | number[] | null = null;
  bysecond: number | number[] | null = null;
  byeaster: number | null = null;

  static create(options: RruleOptions): RruleOptions {
    // console.log('create', { options });
    const form = new RruleOptions();
    form.freq = options.freq;
    form.interval = options.interval ?? 1; // ICal.Net leaves interval out if 1. eg. RRULE:FREQ=DAILY;WKST=SU;COUNT=15
    form.count = options.count ?? null;
    form.until = options.until ?? null;
    form.bysetpos = options.bysetpos ?? null;
    form.bymonth = options.bymonth ?? null;
    form.bymonthday = options.bymonthday ?? null;
    form.byyearday = options.byyearday ?? null;
    form.byweekno = options.byweekno ?? null;
    form.byweekday = options.byweekday ?? null;
    form.byhour = options.byhour ?? null;
    form.byminute = options.byminute ?? null;
    form.bysecond = options.bysecond ?? null;
    form.byeaster = options.byeaster ?? null;
    form.wkst = options.wkst ?? RRule.SU; // #446006 EPG_Issue: Issue with creating a recurring event using weekly option.  Set all events for week to start on Sunday unless already set in ics
    return form;
  }
}
export type MonthMode = 'onMonthDay' | 'onWeek';

export class Field {
  name: string;

  isEditable: boolean;

  constructor(name, isEditable) {
    this.name = name;
    this.isEditable = isEditable;
  }
}
export class FieldManager {
  fields: { [key: string]: boolean };

  constructor() {
    this.fields = {};
  }

  // Method to add a field
  addField(name, isEditable) {
    const field = new Field(name, isEditable);
    this.fields[name] = field.isEditable;
  }

  // Method to convert fields to a dictionary
  static getFileds(allowCustomTextEditable: boolean) {
    const manager = new FieldManager();
    manager.addField('description', allowCustomTextEditable);
    manager.addField('categories', allowCustomTextEditable);
    manager.addField('rating-mpaa', allowCustomTextEditable);
    manager.addField('rating-bbfc', allowCustomTextEditable);
    manager.addField('episode-xmltv', allowCustomTextEditable);
    manager.addField('episode-onscreen', allowCustomTextEditable);
    manager.addField('us-parental-ratings', allowCustomTextEditable);
    manager.addField('european-fsk-rating', allowCustomTextEditable);
    manager.addField('network-value', allowCustomTextEditable);
    manager.addField('program-type', allowCustomTextEditable);
    manager.addField('original-air-date', allowCustomTextEditable);
    manager.addField('team-name', allowCustomTextEditable);
    manager.addField('league-name', allowCustomTextEditable);
    manager.addField('tmsid-episode', allowCustomTextEditable);
    manager.addField('tmsid-series', allowCustomTextEditable);
    manager.addField('actor', allowCustomTextEditable);
    manager.addField('producer', allowCustomTextEditable);
    manager.addField('director', allowCustomTextEditable);
    manager.addField('genre', allowCustomTextEditable);
    manager.addField('season', allowCustomTextEditable);
    manager.addField('episode', allowCustomTextEditable);
    manager.addField('series', allowCustomTextEditable);
    manager.addField('subTitle', allowCustomTextEditable);
    return manager.toDictionary();
  }

  toDictionary() {
    return this.fields;
  }
}

export class EventForm {
  mode: 'new' | 'existing' = 'new';
  id: string = '';
  title: string | null = '';
  startTimezone: string | null = '';
  start: Date | null = null;
  endTimezone: string | null = '';
  end: Date | null = null;
  exdate: string[] = [];
  categories: string[] = [];
  allDay: boolean = false;
  description: string | null = '';
  timezone: string = 'America/Los_Angeles';
  rrule: RruleOptions;
  location: string | null = '';
  recurrenceRule: string | null = '';
  monthMode: 'onMonthDay' | 'onWeek' = 'onMonthDay';
  overlap: boolean = false;
  duration: string = '01:00:00';
  attachedFuelEntityType: string | null = null;
  attachedFuelEntityId: string | null = null;
  color: string | null = '';
  copyable: boolean = false;
  attachedFuelDisplayClassName: string | null = null;
  timeStamps: string | null = '';
  enableNestedVideoMetaData: boolean = false;
  attchedFuelEventFlags: string | null = null;
  attachedFuelGraphicsConfig: string | null = null;
  graphicsUrl: string | null = null;
  attachedFuelCustomAttributes: string | null = null;
  customAttributes: CustomAttributes = new CustomAttributes();
  item: Item | null = null;
  parentEntityName: string | null = '';
  eventUsedFor: string | null = 'Channel';
  allowCustomTextInEpg: boolean = false;
  fieldDictionary: { [key: string]: boolean } = {};
  allowCustomTextEditable: boolean = false;
  existingExcludeEvent: any = null;
  subTitle: string | null = '';

  static create(
    start: Date,
    end: Date,
    timezone: string,
    isRepeat: boolean = false,
    eventUsedFor = 'Channel',
    allowCustomTextEditable = false
  ): EventForm {
    const form = new EventForm();
    form.mode = 'new';
    form.id = uuid();
    form.startTimezone = timezone;
    form.start = start;
    form.endTimezone = timezone;
    form.end = end;
    form.timezone = timezone;
    form.duration = calendarUtils.getDuration(start, end);

    if (isRepeat) {
      form.rrule = new RruleOptions();
      // form.rrule.tzid = 'utc';
      // form.rrule.dtstart =  start;
      const rrule = new RRule(form.rrule);
      // #446006 EPG_Issue: Issue with creating a recurring event using weekly option.  Set all events for week to start on Sunday unless already set in ics
      if (form.rrule.wkst == null) {
        form.rrule.wkst = RRule.SU;
      }
      form.rrule.dtstart = start;
      form.recurrenceRule = rrule.toString();
    }
    if (eventUsedFor === 'Input') {
      form.attachedFuelEntityType = 'LiveProgram';
    }
    if (eventUsedFor === 'Channel') {
      form.fieldDictionary = FieldManager.getFileds(allowCustomTextEditable);
    }

    return form;
  }

  static checkEvent(eventForm: EventForm, daysToKeep) {
    // console.log('checkEvent', { eventForm, daysToKeep });

    if (daysToKeep != -1) {
      // #207 aalt 2024-10-21 we always keep one day just to be safe we are not cutting off live content currently streaming
      if (daysToKeep < 1) {
        daysToKeep = 1;
      }

      const cutOffTime = DateTime.now().setZone(eventForm.timezone).minus({ days: daysToKeep }).valueOf();
      // console.log('cutOffTime', {cutOffTime,eventForm});
      // const cutOffTime1 =  DateTime.now().minus({ days: daysToKeep }).valueOf();
      // console.log({cutOffTime, cutOffTime1})
      const endTime = DateTime.fromISO(eventForm.end).valueOf();
      if ((eventForm.recurrenceRule == null || eventForm.recurrenceRule == '') && endTime < cutOffTime) {
        // console.log('old single event', { cutOffTime, endTime, eventForm });
        return false;
      }
      if (eventForm.recurrenceRule !== '' && eventForm.rrule && eventForm.rrule.until) {
        const until = DateTime.fromJSDate(eventForm.rrule.until).setZone(eventForm.timezone).valueOf();
        // console.log('old recurring event', { cutOffTime, until, eventForm });
        if (until < cutOffTime) {
          // console.log('old recurring event', { cutOffTime, until, eventForm });
          return false;
        }
      }
      // this logic is incomplete as you can have a recurrence count by month or week, not just days, as well as have an interval > 1
      // else if (eventForm.recurrenceRule !== '' && eventForm.rrule && eventForm.rrule.count && eventForm.rrule.count > 0) {
      //   const until = DateTime.fromISO(eventForm.start).plus({ days: eventForm.rrule.count }).valueOf();
      //   if (until < cutOffTime) {
      //     // console.log('old recurring event', { cutOffTime, until, eventForm });
      //     return false;
      //   }
      // }
    }
    return true;
  }

  static mergeCustomAttributes(sourceAttributesEncode, targetAttributes, fieldDictionary) {
    const icsAttachedFuelCustomAttributes = sourceAttributesEncode;
    if (icsAttachedFuelCustomAttributes != null && !clientUtils.isNullOrWhitespace(icsAttachedFuelCustomAttributes)) {
      const decodedIcsCustomAttributes = compressionUtils.base64Decode(icsAttachedFuelCustomAttributes);
      const icsCustomAttributesObj = JSON.parse(decodedIcsCustomAttributes);
      for (const key in icsCustomAttributesObj) {
        // Check if the key exists in the target object
        if (targetAttributes.hasOwnProperty(key)) {
          // Assign the value from the source object to the target object
          targetAttributes[key] = icsCustomAttributesObj[key];
          fieldDictionary[key] = true;
        }
      }
    }
    return { targetAttributes, fieldDictionary };
  }

  static convertIcsJsonToEventForm(
    eventObj: any,
    timezone,
    eventUsedFor = '',
    clips: Item[] = [],
    allLivePrograms: LiveProgram[] = [],
    allowCustomTextEditable = true
  ) {
    // console.log('convertIcsJsonToEventForm', { eventObj, timezone, clips, allLivePrograms });
    const form = new EventForm();
    form.mode = 'existing';
    form.id = eventObj.id;
    // compatibilty with existing epg events i.e if date format is coming i.e 20230223T030000
    form.startTimezone = eventObj.startTimezone;
    form.start = dateUtils.convertDateStrLocalToTimeZoneSpecificDate(eventObj.start, timezone);
    form.endTimezone = eventObj.endTimezone;
    form.end = dateUtils.convertDateStrLocalToTimeZoneSpecificDate(eventObj.end, timezone);
    form.duration = calendarUtils.getDuration(form.start, form.end);
    form.timezone = timezone;
    if (eventUsedFor === 'Channel') {
      form.fieldDictionary = FieldManager.getFileds(allowCustomTextEditable);
    }

    form.title = eventObj.title;
    form.location = eventObj.location;

    if (eventObj.attachedFuelEntityId && clientUtils.parseFuelEntityType(eventObj.attachedFuelEntityId)) {
      form.attachedFuelEntityType = clientUtils.parseFuelEntityType(eventObj.attachedFuelEntityId);
      form.color = calendarUtils.getEventColor(form.attachedFuelEntityType);
      form.attachedFuelEntityId = eventObj.attachedFuelEntityId;
    }

    if (eventUsedFor === 'ScheduleDisplayViewer') {
      form.color = eventObj.color;
      form.parentEntityName = eventObj.parentEntityName;
    }
    if (eventObj.exdate && eventObj.exdate.length > 0) {
      form.exdate = uniq(eventObj.exdate);
      // #2546 aalt 2025-01-17 Don't convert exdates to local OU timezone, keep original timezone
      // form.exdate = form.exdate.map((date) => dateUtils.convertDateStrLocalToTimeZoneSpecificDate(date, timezone));
    }
    if (eventObj.attachedFuelDisplayClassName) {
      form.attachedFuelDisplayClassName = eventObj.attachedFuelDisplayClassName;
    }
    if (eventObj.attchedFuelEventFlags) {
      const obj = clientUtils.parseFuelFlags(eventObj.attchedFuelEventFlags);
      form.attchedFuelEventFlags = eventObj.attchedFuelEventFlags;
      form.enableNestedVideoMetaData = obj.enableNestedVideoMetaData;
    }

    let tempGraphicsUrl = null;
    if (eventObj.attachedFuelGraphicsConfig) {
      form.attachedFuelGraphicsConfig = eventObj.attachedFuelGraphicsConfig;

      const decodedGraphicsConfig = compressionUtils.base64Decode(eventObj.attachedFuelGraphicsConfig);
      const attachedFuelGraphicsConfigObj = JSON.parse(decodedGraphicsConfig);
      tempGraphicsUrl = attachedFuelGraphicsConfigObj.url;
    }
    form.graphicsUrl = tempGraphicsUrl;

    if (eventObj.hasOwnProperty('categories')) {
      form.categories = eventObj.categories === '' ? [] : eventObj.categories.split(',');
      form.fieldDictionary.categories = true;
    }

    if (eventObj.attachedFuelCustomAttributes) {
      form.attachedFuelCustomAttributes = eventObj.attachedFuelCustomAttributes;
      const item = EventForm.mergeCustomAttributes(
        form.attachedFuelCustomAttributes,
        form.customAttributes,
        form.fieldDictionary
      );
      form.customAttributes = item.targetAttributes;
      form.fieldDictionary = item.fieldDictionary;
    }
    if (eventObj.attachedFuelEntityType === 'ScheduleLiveProgramDisplay' && eventObj.color) {
      form.color = eventObj.color;
    }

    // console.log('convertIcsJsonToEventForm', eventObj.subTitle);
    if (eventObj.hasOwnProperty('description')) {
      form.description =
        typeof eventObj.description !== 'undefined' && eventObj.description
          ? eventObj.description
              .replace(/\r\n\t/g, '\n')
              .replace(/\\+\\n\\+\\n/g, '')
              .replace(/\\+\\n/g, '')
              .replace(/\\n/g, '\n')
              .replace(/\\*\\;/g, ';')
              .replace(/\\*\\,/g, ',')
          : '';
      form.fieldDictionary.description = true;
    }
    if (eventObj.hasOwnProperty('subTitle')) {
      form.subTitle =
        typeof eventObj.subTitle !== 'undefined' && eventObj.subTitle
          ? eventObj.subTitle
              .replace(/\r\n\t/g, '\n')
              .replace(/\\+\\n\\+\\n/g, '')
              .replace(/\\+\\n/g, '')
              .replace(/\\n/g, '\n')
              .replace(/\\*\\;/g, ';')
              .replace(/\\*\\,/g, ',')
          : '';
      form.fieldDictionary.subTitle = true;
    }

    if (
      (form.attachedFuelEntityType === 'Clip' ||
        form.attachedFuelEntityType === 'ClipGroup' ||
        form.attachedFuelEntityType === 'NestedChannel') &&
      clips.length > 0
    ) {
      const clipId = clientUtils.parseFuelEntityId(eventObj.attachedFuelEntityId);
      const clip = clips.find((clip) => clip.id === clipId);
      if (!form.fieldDictionary.description && clip) {
        form.description = clip?.data.businessMetadata?.synopsis;
      }
      if (!form.fieldDictionary.subTitle && clip) {
        form.subTitle = clip?.data.businessMetadata?.subTitle;
      }
      // console.log('form.categories', form.categories)
      if (clip && !form.fieldDictionary.categories) {
        form.categories = clip?.categories.map((c) => c.name);
      }
      const icsAttachedFuelCustomAttributes = form.attachedFuelCustomAttributes;
      const clipCustomAttributesObj = FuelCalendarHelper.extractCustomAttributesFromTags(clip && clip.tags ? clip.tags : []);
      form.attachedFuelCustomAttributes = eventObj.attachedFuelCustomAttributes ?? null;
      const item = EventForm.mergeCustomAttributes(
        form.attachedFuelCustomAttributes,
        clipCustomAttributesObj,
        form.fieldDictionary
      );
      form.customAttributes = item.targetAttributes;
      form.fieldDictionary = item.fieldDictionary;
    }
    if (form.attachedFuelEntityType === 'LiveProgram' && eventUsedFor === 'Channel') {
      const liveProgramId = clientUtils.parseFuelEntityId(eventObj.attachedFuelEntityId);
      const liveProgram = allLivePrograms.find((lp) => lp.id === liveProgramId);
      if (!form.fieldDictionary.description && liveProgram) {
        form.description = liveProgram.description;
      }
      if (!form.fieldDictionary.categories && liveProgram) {
        form.categories = liveProgram.metadata?.categories.map((c) => c.name);
      }
    }
    if (eventObj.rrule) {
      // console.log('convertIcsJsonToEventForm', { recurrenceRule: eventObj.rrule });
      const options: RruleOptions = RRule.parseString(eventObj.rrule);
      form.rrule = RruleOptions.create(options);
      const rrule = new RRule(cloneDeep(form.rrule));
      // the old way of generating the RecurrenceRule left no chance to clean up attributes but took the string straight from eventObj.rrule
      // eg. rruel: "FREQ=DAILY;INTERVAL=1;UNTIL=20230823T014939Z"
      // form['oldRecurrenceRule'] = `RRULE:${eventObj.rrule}`;
      form.recurrenceRule = rrule.toString();
      // don't add the dtstart property until after calling .toString() above as dtstart adds some text before the RRULE: part
      form.rrule.dtstart = form.start;
    }
    // console.log('form', form);

    // aalt 2024-10-16 Leave this block at the end as a catch all, as description needs to be set to empty string by default for synthetic epg to work.
    if (form.description == null) {
      form.description = '';
    }

    return form;
  }
}
